import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, Backdrop, CircularProgress } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import CompetitionForm from "../../component/admin/formComponents/CompetitionForm";
import { parseCompetitionRegistrationEnabled } from "../../utils/competitionRegistration";
import { getCompetition } from "../../api/getCompetition";
import { toCompetitionDateTimeInputValue } from "../../utils/competitionDateTime";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  description: Yup.string().required("Description is required"),
  image: Yup.mixed().required("Banner image is required"),
  video: Yup.mixed().required("Promotional video is required"),
  start_date: Yup.string().required("Start date is required"),
  end_date: Yup.string()
    .required("End date is required")
    .test("after-start", "End date must be after start date", function (value) {
      const { start_date } = this.parent;
      if (!start_date || !value) return true;
      return new Date(value) > new Date(start_date);
    }),
  categories: Yup.array()
    .of(
      Yup.string()
        .trim()
        .required("Category name is required")
        .max(100, "Category name must be 100 characters or less")
    )
    .min(1, "Add at least one category")
    .max(10, "Maximum 10 categories"),
  judges_percent: Yup.number().min(0).max(100).required("Judges % is required"),
  users_percent: Yup.number()
    .min(0)
    .max(100)
    .required("Users % is required")
    .test("sum-100", "Judges % + Users % must equal 100", function (value) {
      return Number(this.parent.judges_percent) + Number(value) === 100;
    }),
  max_free_votes_per_day: Yup.number()
    .min(1, "Must be at least 1")
    .required("Free votes per day is required"),
  registration_enabled: Yup.boolean(),
  votes_purchase_enabled: Yup.boolean(),
  vote_purchase_currency: Yup.string().max(3),
  vote_pack_5_votes: Yup.number().min(1, "Must be at least 1 vote"),
  vote_pack_10_votes: Yup.number().min(1, "Must be at least 1 vote"),
  vote_pack_50_votes: Yup.number().min(1, "Must be at least 1 vote"),
  vote_pack_5_price: Yup.number()
    .min(0, "Price must be 0 or more")
    .when("votes_purchase_enabled", {
      is: true,
      then: (s) => s.min(0.01, "Price must be greater than 0 when payments are enabled"),
    }),
  vote_pack_10_price: Yup.number()
    .min(0, "Price must be 0 or more")
    .when("votes_purchase_enabled", {
      is: true,
      then: (s) => s.min(0.01, "Price must be greater than 0 when payments are enabled"),
    }),
  vote_pack_50_price: Yup.number()
    .min(0, "Price must be 0 or more")
    .when("votes_purchase_enabled", {
      is: true,
      then: (s) => s.min(0.01, "Price must be greater than 0 when payments are enabled"),
    }),
  events: Yup.array()
    .min(1, "Add at least one event")
    .max(10, "Maximum 10 events")
    .of(
      Yup.object().shape({
        name: Yup.string().required("Event name is required"),
      })
    ),
  rounds: Yup.array().max(10, "Maximum 10 rounds"),
});

export default function EditCompetition() {
  const token = useSelector((state) => state.auth.token);
  const [loading, setLoading] = useState(true);
  const [votingLocked, setVotingLocked] = useState(false);
  const [competitionStatus, setCompetitionStatus] = useState("scheduled");
  const originalStartRef = useRef("");
  const navigate = useNavigate();
  const { id } = useParams();

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      image: "",
      video: "",
      start_date: "",
      end_date: "",
      categories: [],
      judges_percent: 70,
      users_percent: 30,
      max_free_votes_per_day: 1,
      registration_enabled: true,
      votes_purchase_enabled: false,
      vote_purchase_currency: "GBP",
      vote_pack_5_votes: 5,
      vote_pack_5_price: 1,
      vote_pack_10_votes: 10,
      vote_pack_10_price: 2,
      vote_pack_50_votes: 50,
      vote_pack_50_price: 10,
      events: [{ name: "" }],
      rounds: [],
    },
    validate: (values) => {
      try {
        validationSchema.validateSync(values, {
          abortEarly: false,
          context: { originalStart: originalStartRef.current },
        });
        return {};
      } catch (err) {
        const errors = {};
        if (err.inner) {
          err.inner.forEach((e) => {
            if (e.path && !errors[e.path]) errors[e.path] = e.message;
          });
        }
        return errors;
      }
    },
    onSubmit: async (values) => {
      setLoading(true);
      const data = new FormData();
      data.append("id", id);
      data.append("name", values.name);
      data.append("description", values.description);
      data.append("start_date", values.start_date);
      data.append("end_date", values.end_date);
      data.append("judges_percent", values.judges_percent);
      data.append("users_percent", values.users_percent);
      data.append("max_free_votes_per_day", values.max_free_votes_per_day);
      data.append(
        "registration_enabled",
        parseCompetitionRegistrationEnabled(values.registration_enabled)
          ? "true"
          : "false"
      );
      data.append("votes_purchase_enabled", values.votes_purchase_enabled);
      data.append("vote_purchase_currency", values.vote_purchase_currency);
      data.append("vote_pack_5_votes", values.vote_pack_5_votes);
      data.append("vote_pack_5_price", values.vote_pack_5_price);
      data.append("vote_pack_10_votes", values.vote_pack_10_votes);
      data.append("vote_pack_10_price", values.vote_pack_10_price);
      data.append("vote_pack_50_votes", values.vote_pack_50_votes);
      data.append("vote_pack_50_price", values.vote_pack_50_price);
      data.append(
        "categories",
        JSON.stringify(
          values.categories.map((c) => String(c).trim()).filter(Boolean)
        )
      );
      data.append(
        "events",
        JSON.stringify(values.events.map((e) => ({ name: e.name })))
      );
      // Final Night rounds are managed on the Final Night Control page only.
      if (values.image instanceof File) {
        data.append("image", values.image);
      }
      if (values.video instanceof File) {
        data.append("video", values.video);
      }

      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/editCompetition`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.success) {
          navigate("/admin/dashboard/competitions", {
            state: {
              toastMessage:
                response.data.message || "Competition updated successfully",
              toastType: "success",
            },
          });
        } else {
          toast.error(response.data.message);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Error updating competition");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getCompetition(id, token);
        if (!data.success) {
          toast.error(data.message);
          navigate("/admin/dashboard/competitions");
          return;
        }
        const c = data.data;
        setVotingLocked(Boolean(c.voting_locked));
        setCompetitionStatus(c.status || "scheduled");
        const startVal = toCompetitionDateTimeInputValue(c.start_date);
        originalStartRef.current = startVal;
        formik.setValues({
          name: c.name || "",
          description: c.description || "",
          image: c.banner_img || "",
          video: c.promo_video || "",
          start_date: startVal,
          end_date: toCompetitionDateTimeInputValue(c.end_date),
          categories: (c.categories || []).map((x) => x.category),
          judges_percent: c.judges_percent,
          users_percent: c.users_percent,
          max_free_votes_per_day:
            c.max_free_votes_per_day ?? c.max_votes_per_event ?? 1,
          registration_enabled: parseCompetitionRegistrationEnabled(
            c.registration_enabled
          ),
          votes_purchase_enabled: Boolean(c.votes_purchase_enabled),
          vote_purchase_currency: c.vote_purchase_currency || "GBP",
          vote_pack_5_votes: Number(c.vote_pack_5_votes ?? 5),
          vote_pack_5_price: Number(c.vote_pack_5_price ?? 1),
          vote_pack_10_votes: Number(c.vote_pack_10_votes ?? 10),
          vote_pack_10_price: Number(c.vote_pack_10_price ?? 2),
          vote_pack_50_votes: Number(c.vote_pack_50_votes ?? 50),
          vote_pack_50_price: Number(c.vote_pack_50_price ?? 10),
          events:
            c.events?.length > 0
              ? [...c.events]
                  .sort((a, b) => (a.event_order || 0) - (b.event_order || 0))
                  .map((e) => ({ name: e.name }))
              : [{ name: "" }],
          rounds:
            c.rounds?.length > 0
              ? [...c.rounds]
                  .sort((a, b) => (a.round_order || 0) - (b.round_order || 0))
                  .map((r) => ({
                    name: r.name,
                    judges: (r.judges || [])
                      .map((j) => j.judgeUser)
                      .filter(Boolean)
                      .map((u) => ({
                        ...u,
                        profileImage: u.profile_img || u.profileImage,
                      })),
                  }))
              : [],
        });
      } catch (e) {
        toast.error(
          e?.response?.data?.message || e?.message || "Failed to load competition"
        );
        navigate("/admin/dashboard/competitions");
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  return (
    <>
      <ToastContainer />
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress sx={{ color: "#ef364e" }} />
      </Backdrop>
      <Box className="free_votes_page">
        <Box className="page_title">
          <Typography variant="h1" component="h1">
            Edit Competition
          </Typography>
          <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 0.5 }}>
            Step 1–2 setup: competition details + Final Night scoring structure.
            Live stream and round control are on Final Night Control after Stop.
          </Typography>
        </Box>
        {!loading ? (
          <CompetitionForm
            formik={formik}
            edited={true}
            votingLocked={votingLocked}
            competitionStatus={competitionStatus}
            competitionId={id}
          />
        ) : null}
      </Box>
    </>
  );
}
